import { action } from '@ember/object';
import { service } from '@ember/service';
import { compare } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import ContactModel from 'donor-elf-web/models/contact';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import AddSomethingModal, {
  type TaskAddedArgs,
} from 'donor-elf-web/services/add-something-modal';
import TaskService from 'donor-elf-web/services/task-service';

interface ComponentArgs {
  contact: ContactModel;
  model: V2TaskModel[];
}

export default class ContactV2TasksComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);

    this.tasks = this.args.model;
    this.sortTheTasks();
  }

  @service declare addSomethingModal: AddSomethingModal;
  @service declare taskService: TaskService;

  tasks: V2TaskModel[];
  @tracked sortedTasks: V2TaskModel[] = [];
  addTaskSubsciptionToken?: string;

  get showCard() {
    return this.sortedTasks.length > 0;
  }

  // - new tasks are always at the top
  // - next come unfinished tasks
  //   - overdue
  //   - no due date (dueDate is considered today for sorting purposes)
  //   - due date in the future
  // - finally finished tasks sorted by completedDate
  sortTheTasks() {
    let today = new Date();
    this.sortedTasks = this.tasks.sort((a, b) => {
      if (a.isNew) {
        return -1;
      }
      if (b.isNew) {
        return 1;
      }

      if (a.completedDate) {
        if (b.completedDate) {
          return compare(b.completedDate, a.completedDate);
        } else {
          // finished goes after unfinished
          return 1;
        }
      } else if (b.completedDate) {
        // unfinished goes before unfinished
        return -1;
      } else {
        // both unfinished
        return compare(a.dueDate || today, b.dueDate || today);
      }
    });
  }

  @action
  templateDidInsert() {
    this.unsubscribeFromAddTaskIfPresent();
    this.addTaskSubsciptionToken = this.addSomethingModal.subscribeToTaskAdded(
      this.handleTaskAddedFromAddModal,
    );
  }

  @action
  templateWillDestroy() {
    this.unsubscribeFromAddTaskIfPresent();
  }

  unsubscribeFromAddTaskIfPresent() {
    if (this.addTaskSubsciptionToken) {
      this.addSomethingModal.unsubscribeWithToken(this.addTaskSubsciptionToken);
      this.addTaskSubsciptionToken = undefined;
    }
  }

  @action
  handleTaskAddedFromAddModal(_message: string, data: TaskAddedArgs) {
    if (data.task.contactId === this.args.contact.id) {
      this.tasks = [data.task, ...this.tasks];
      // not sorting because we always want the new task to be at the top
      this.sortedTasks = [data.task, ...this.sortedTasks];
    }
  }

  @action
  addTask() {
    this.addSomethingModal.open({
      type: 'task',
    });
  }

  @action
  handleCancelEditTask(task: V2TaskModel) {
    if (task.isNew) {
      this.tasks = this.tasks.filter((x) => x !== task);
      this.sortTheTasks();
    }
  }

  @action
  handleTaskDeleted(task: V2TaskModel) {
    this.tasks = this.tasks.filter((x) => x !== task);
    this.sortTheTasks();
  }

  @action
  handleTaskSaved() {
    // intentionally not doing anything so the tasks won't resort
  }
}
